import type {
  DecisionConsoleBetV1,
  DecisionConsoleDocketItemV1,
  DecisionConsoleDriftStatus,
  DecisionConsoleOptionV1,
  DecisionConsoleReadModelV1,
} from './decision-console';
import type { WatchEvent } from './leading-indicator-watch';

export const DRIFT_MONITORING_EXPERIENCE_SCHEMA_VERSION =
  'drift_monitoring_experience_v1' as const;

export type DriftTone = 'positive' | 'caution' | 'critical' | 'info' | 'neutral';

export const DRIFT_INTERVENTION_VERBS = [
  'approve',
  'modify',
  'defer',
  'escalate',
] as const;
export type DriftInterventionVerb = (typeof DRIFT_INTERVENTION_VERBS)[number];

export type DriftRegionState = 'ready' | 'degraded';
export type DriftNamedGap = { code: string; message: string };

export type DriftVerdictHeroV1 = {
  eyebrow: string;
  headline: string;
  atRiskUsd: number | null;
  atRiskDisplay: string;
  basisLabel: string;
  tone: DriftTone;
  supportingLine: string | null;
  withheld: DriftNamedGap | null;
};

export type DriftControlPlanCellV1 = {
  key: string;
  label: string;
  statusLabel: string;
  tone: DriftTone;
  detail: string | null;
  slipped: boolean;
};

export type DriftRoutingAnnotationV1 = {
  id: string;
  label: string;
  message: string;
  asOf: string | null;
  sourceRef: string | null;
  origin: 'watch_event' | 'control_plan';
};

export type DriftControlPlanV1 = {
  eyebrow: string;
  cadenceLabel: string;
  cells: DriftControlPlanCellV1[];
  routing: DriftRoutingAnnotationV1[];
  state: DriftRegionState;
  gap: DriftNamedGap | null;
  watchGap: DriftNamedGap | null;
};

export type DriftInterventionRowV1 = {
  id: string;
  driftSignal: string;
  driftMagnitude: string | null;
  rootCause: string;
  ownerLabel: string;
  ownerAssigned: boolean;
  recommendedIntervention: string;
  priceLabel: string | null;
  decisionRequiredBy: string[];
  tone: DriftTone;
  verbs: readonly DriftInterventionVerb[];
  origin: 'commitment_drift' | 'watch_event';
};

export type DriftInterventionQueueV1 = {
  eyebrow: string;
  rows: DriftInterventionRowV1[];
  state: DriftRegionState;
  gap: DriftNamedGap | null;
};

export type DriftLedgerThresholdV1 = {
  metricKey: string;
  label: string;
  unit: string;
  warningThresholdRatio: number | null;
  driftThresholdRatio: number | null;
  amendmentThresholdRatio: number | null;
  targetAt: string | null;
};

export type DriftLedgerReceiptV1 = {
  ledgerId: string | null;
  actor: string | null;
  timestamp: string | null;
  rationale: string | null;
  acceptedAssumptions: string[];
  driftThresholds: DriftLedgerThresholdV1[];
  reviewDate: string | null;
  absentFields: { field: string; reason: string }[];
  state: DriftRegionState;
};

export type DriftSoWhatV1 = { lead: string; detail: string };

export type DriftMonitoringExperienceV1 = {
  schemaVersion: typeof DRIFT_MONITORING_EXPERIENCE_SCHEMA_VERSION;
  weekLabel: string;
  verdict: DriftVerdictHeroV1;
  controlPlan: DriftControlPlanV1;
  interventionQueue: DriftInterventionQueueV1;
  ledger: DriftLedgerReceiptV1;
  soWhat: DriftSoWhatV1;
};

export type DriftLedgerInput = {
  ledgerId?: string | null;
  actor?: string | null;
  timestamp?: string | null;
  rationale?: string | null;
  acceptedAssumptions?: string[];
  driftThresholds?: DriftLedgerThresholdV1[];
  reviewDate?: string | null;
};

export type BuildDriftMonitoringExperienceInput = {
  readModel: DecisionConsoleReadModelV1;
  watchEvents?: readonly WatchEvent[];
  ledger?: DriftLedgerInput;
  controlPlanCells?: DriftControlPlanCellV1[];
  weekLabel?: string;
};

const DRIFT_STATUSES: ReadonlySet<DecisionConsoleDriftStatus> = new Set([
  'watch',
  'drifting',
  'amendment_required',
]);

const SO_WHAT: DriftSoWhatV1 = {
  lead: 'Drift is not a status update; it is the next leadership decision.',
  detail:
    'After approval the decision becomes a monitored commitment. When reality drifts, ' +
    'the next decision is routed back to leadership: what should change next cycle?',
};

const UNASSIGNED_OWNER = 'Unassigned' as const;
const OWNER_PLACEHOLDERS = new Set(['', 'owner to assign', 'unassigned', 'tbd', '-']);

export function buildDriftMonitoringExperience(
  input: BuildDriftMonitoringExperienceInput,
): DriftMonitoringExperienceV1 {
  const watchEvents = input.watchEvents ?? [];
  const weekLabel = input.weekLabel ?? 'This cycle';
  return {
    schemaVersion: DRIFT_MONITORING_EXPERIENCE_SCHEMA_VERSION,
    weekLabel,
    verdict: buildVerdictHero(input.readModel, weekLabel),
    controlPlan: buildControlPlan(
      input.readModel,
      watchEvents,
      weekLabel,
      input.controlPlanCells,
    ),
    interventionQueue: buildInterventionQueue(input.readModel, watchEvents, weekLabel),
    ledger: buildLedgerReceipt(input.ledger),
    soWhat: SO_WHAT,
  };
}

function buildVerdictHero(
  model: DecisionConsoleReadModelV1,
  weekLabel: string,
): DriftVerdictHeroV1 {
  const atRiskUsd = finiteOrNull(model.riskRadar.valueAtRiskUsd);
  const basisLabel = model.riskRadar.valueAtRiskLabel ?? 'approved value binding at risk';
  const headline = model.executiveVerdict?.headline ?? 'Commitment under active monitoring';
  if (atRiskUsd === null) {
    return {
      eyebrow: `Realization verdict - ${weekLabel}`,
      headline,
      atRiskUsd: null,
      atRiskDisplay: '-',
      basisLabel,
      tone: 'neutral',
      supportingLine: model.executiveVerdict?.bindingConstraint ?? null,
      withheld: {
        code: 'value_at_risk_withheld',
        message: 'Dollars at risk withheld because no governed value binding is ready.',
      },
    };
  }
  return {
    eyebrow: `Realization verdict - ${weekLabel}`,
    headline,
    atRiskUsd,
    atRiskDisplay: formatUsd(atRiskUsd),
    basisLabel,
    tone: atRiskUsd > 0 ? 'critical' : 'positive',
    supportingLine: model.executiveVerdict?.bindingConstraint ?? null,
    withheld: null,
  };
}

function buildControlPlan(
  model: DecisionConsoleReadModelV1,
  watchEvents: readonly WatchEvent[],
  weekLabel: string,
  override?: DriftControlPlanCellV1[],
): DriftControlPlanV1 {
  const cells = override ?? deriveControlPlanCells(model.bets);
  const routing = buildRouting(model.bets, watchEvents);
  const watchGap =
    watchEvents.length === 0
      ? {
          code: 'no_active_fired_watches',
          message: 'No leading-indicator watches fired in this cycle.',
        }
      : null;
  if (cells.length === 0) {
    return {
      eyebrow: `Control plan - ${weekLabel}`,
      cadenceLabel: 'Monitoring active weekly',
      cells: [],
      routing,
      state: 'degraded',
      gap: {
        code: 'no_monitoring_configured',
        message: 'No commitments are under watch yet.',
      },
      watchGap,
    };
  }
  return {
    eyebrow: `Control plan - ${weekLabel}`,
    cadenceLabel: 'Monitoring active weekly',
    cells,
    routing,
    state: 'ready',
    gap: null,
    watchGap,
  };
}

function deriveControlPlanCells(
  bets: readonly DecisionConsoleBetV1[],
): DriftControlPlanCellV1[] {
  return bets.slice(0, 6).map((bet) => ({
    key: bet.key,
    label: bet.name,
    statusLabel: driftStatusLabel(bet.drift.status),
    tone: toneForStatus(bet.drift.status),
    detail: behindDetail(bet),
    slipped: DRIFT_STATUSES.has(bet.drift.status),
  }));
}

function buildRouting(
  bets: readonly DecisionConsoleBetV1[],
  watchEvents: readonly WatchEvent[],
): DriftRoutingAnnotationV1[] {
  const routing: DriftRoutingAnnotationV1[] = watchEvents.map((event) => ({
    id: `watch:${event.id}`,
    label: event.watchLabel,
    message: `Drift detected - ${event.suggestedQuestion} - routing to intervention queue below`,
    asOf: event.asOf,
    sourceRef: event.sourceRef ?? sourceRefFromEvent(event),
    origin: 'watch_event' as const,
  }));
  const slipped = bets.filter((bet) => DRIFT_STATUSES.has(bet.drift.status));
  if (slipped.length > 0) {
    const noun = slipped.length === 1 ? 'commitment' : 'commitments';
    routing.push({
      id: 'control-plan:slipped',
      label: `${slipped.length} ${noun} slipped`,
      message: 'Drift detected - routing to intervention queue below',
      asOf: latestAsOf(slipped),
      sourceRef: null,
      origin: 'control_plan',
    });
  }
  return routing;
}

function buildInterventionQueue(
  model: DecisionConsoleReadModelV1,
  watchEvents: readonly WatchEvent[],
  weekLabel: string,
): DriftInterventionQueueV1 {
  const docketByBet = new Map<string, DecisionConsoleDocketItemV1>();
  for (const item of model.decisionDocket) {
    if (!docketByBet.has(item.linkedBetKey)) docketByBet.set(item.linkedBetKey, item);
  }
  const rows: DriftInterventionRowV1[] = [];
  const coveredCommitmentIds = new Set<string>();
  for (const bet of model.bets) {
    if (!DRIFT_STATUSES.has(bet.drift.status)) continue;
    coveredCommitmentIds.add(bet.commitmentId);
    coveredCommitmentIds.add(bet.decisionId);
    rows.push(interventionRowFromBet(bet, docketByBet.get(bet.key) ?? null));
  }
  for (const event of watchEvents) {
    if (
      event.affectedIdentity.kind === 'commitment' &&
      coveredCommitmentIds.has(event.affectedIdentity.id)
    ) {
      continue;
    }
    rows.push(interventionRowFromWatchEvent(event));
  }
  if (rows.length === 0) {
    return {
      eyebrow: `Weekly intervention queue - ${weekLabel} - 0 items requiring leadership decision`,
      rows: [],
      state: 'degraded',
      gap: {
        code: 'no_interventions',
        message: 'No interventions required this cycle; every commitment is holding.',
      },
    };
  }
  const itemNoun = rows.length === 1 ? 'item' : 'items';
  return {
    eyebrow: `Weekly intervention queue - ${weekLabel} - ${rows.length} ${itemNoun} requiring leadership decision`,
    rows,
    state: 'ready',
    gap: null,
  };
}

function interventionRowFromBet(
  bet: DecisionConsoleBetV1,
  docket: DecisionConsoleDocketItemV1 | null,
): DriftInterventionRowV1 {
  const owner = resolveOwner(docket?.ownerLabel ?? bet.ownerLabel);
  const recommended = pickRecommendedOption(bet.options, docket?.recommendedVerb ?? null);
  return {
    id: `bet:${bet.key}`,
    driftSignal: bet.name,
    driftMagnitude: behindDetail(bet),
    rootCause: bet.drift.gapMessage ?? bet.drift.verdictSentence,
    ownerLabel: owner.label,
    ownerAssigned: owner.assigned,
    recommendedIntervention:
      recommended?.headline ?? 'Recommend intervention; no priced option is bound yet',
    priceLabel: priceLabelFor(bet, docket, recommended),
    decisionRequiredBy: owner.assigned ? [owner.label] : [UNASSIGNED_OWNER],
    tone: toneForStatus(bet.drift.status),
    verbs: DRIFT_INTERVENTION_VERBS,
    origin: 'commitment_drift',
  };
}

function interventionRowFromWatchEvent(event: WatchEvent): DriftInterventionRowV1 {
  return {
    id: `watch:${event.id}`,
    driftSignal: event.affectedIdentity.label,
    driftMagnitude: describeCrossed(event),
    rootCause: `${event.watchLabel} fired; ${event.suggestedQuestion}`,
    ownerLabel: UNASSIGNED_OWNER,
    ownerAssigned: false,
    recommendedIntervention: event.suggestedQuestion,
    priceLabel: null,
    decisionRequiredBy: [UNASSIGNED_OWNER],
    tone: 'caution',
    verbs: DRIFT_INTERVENTION_VERBS,
    origin: 'watch_event',
  };
}

function buildLedgerReceipt(ledger?: DriftLedgerInput): DriftLedgerReceiptV1 {
  const ledgerId = nonEmpty(ledger?.ledgerId);
  const actor = nonEmpty(ledger?.actor);
  const timestamp = nonEmpty(ledger?.timestamp);
  const rationale = nonEmpty(ledger?.rationale);
  const acceptedAssumptions = ledger?.acceptedAssumptions ?? [];
  const driftThresholds = ledger?.driftThresholds ?? [];
  const reviewDate = nonEmpty(ledger?.reviewDate);
  const absentFields: { field: string; reason: string }[] = [];
  const notLogged = 'Not yet logged; approve the package to write the ledger entry.';
  if (!ledgerId) absentFields.push({ field: 'ledgerId', reason: notLogged });
  if (!actor) absentFields.push({ field: 'actor', reason: notLogged });
  if (!timestamp) absentFields.push({ field: 'timestamp', reason: notLogged });
  if (!rationale) absentFields.push({ field: 'rationale', reason: notLogged });
  if (acceptedAssumptions.length === 0) {
    absentFields.push({ field: 'acceptedAssumptions', reason: notLogged });
  }
  if (driftThresholds.length === 0) {
    absentFields.push({
      field: 'driftThresholds',
      reason: 'No drift thresholds are set; configure the control plan to arm monitoring.',
    });
  }
  if (!reviewDate) absentFields.push({ field: 'reviewDate', reason: notLogged });
  return {
    ledgerId,
    actor,
    timestamp,
    rationale,
    acceptedAssumptions,
    driftThresholds,
    reviewDate,
    absentFields,
    state: ledgerId ? 'ready' : 'degraded',
  };
}

function pickRecommendedOption(
  options: readonly DecisionConsoleOptionV1[],
  recommendedVerb: string | null,
): DecisionConsoleOptionV1 | null {
  if (options.length === 0) return null;
  return (
    options.find((option) => option.recommended === true) ??
    options.find((option) => option.verb === recommendedVerb) ??
    options[0]
  );
}

function priceLabelFor(
  bet: DecisionConsoleBetV1,
  docket: DecisionConsoleDocketItemV1 | null,
  option: DecisionConsoleOptionV1 | null,
): string | null {
  if (option?.economicsLine) return option.economicsLine;
  const docketDollars = finiteOrNull(docket?.dollarsAtStakeUsd ?? null);
  if (docketDollars !== null) {
    return `${formatUsd(docketDollars)} ${docket?.dollarBasisLabel ?? 'at stake'}`;
  }
  const missUsd = finiteOrNull(bet.drift.projectedMissUsd);
  if (missUsd !== null) return `${formatUsd(missUsd)} ${bet.drift.valueLabel ?? 'projected miss'}`;
  return null;
}

function resolveOwner(rawOwner: string | null | undefined): {
  label: string;
  assigned: boolean;
} {
  const owner = (rawOwner ?? '').trim();
  if (owner.length === 0 || OWNER_PLACEHOLDERS.has(owner.toLowerCase())) {
    return { label: UNASSIGNED_OWNER, assigned: false };
  }
  return { label: owner, assigned: true };
}

function behindDetail(bet: DecisionConsoleBetV1): string | null {
  const ratio = finiteOrNull(bet.drift.behindRatio);
  if (ratio !== null && ratio > 0) return `${Math.round(ratio * 100)}% behind plan`;
  const units = finiteOrNull(bet.drift.behindUnits);
  if (units !== null && units > 0) {
    return `${formatBehindQuantity(units, bet.burnDown.unit)} behind curve`;
  }
  return null;
}

// Behind amounts render in their own dimension, never as a raw unit token.
// Currency: "$3.8M", time-rated currency: "$3.8M a year", per-OBJECT currency
// rates (usd_per_load, usd_per_shipment): "$61 per load" — money per object,
// never a magnitude (#4008 SFIX defect 1 formatter seam).
const TIME_RATE_DENOMINATORS = new Set(['year', 'quarter', 'month', 'week', 'day', 'annum']);

function formatBehindQuantity(units: number, rawUnit: string): string {
  const normalized = String(rawUnit ?? '').trim().toLowerCase();
  if (normalized === 'usd' || normalized === '$') return formatUsd(units);
  if (normalized === 'usd_per_year') return `${formatUsd(units)} a year`;
  const perObject = /^usd_per_([a-z0-9_]+)$/.exec(normalized);
  if (perObject && !TIME_RATE_DENOMINATORS.has(perObject[1])) {
    const rate = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: Math.abs(units) < 100 ? 2 : 0,
    }).format(units);
    return `$${rate} per ${perObject[1].replace(/_/g, ' ')}`;
  }
  return `${formatCount(units)} ${normalized.replace(/_/g, ' ')}`;
}

function describeCrossed(event: WatchEvent): string | null {
  const crossed = event.crossed;
  if (crossed.kind === 'threshold_cross') {
    return `Crossed ${crossed.direction} threshold to ${formatCount(crossed.value)}`;
  }
  if (Number.isFinite(crossed.deltaPct)) {
    const pct = Math.round(Math.abs(crossed.deltaPct));
    return `${crossed.direction === 'up' ? 'Up' : 'Down'} ${pct}% vs baseline`;
  }
  return `Trend ${crossed.direction}`;
}

function driftStatusLabel(status: DecisionConsoleDriftStatus): string {
  switch (status) {
    case 'on_track':
      return 'On track';
    case 'watch':
      return 'Watch';
    case 'drifting':
      return 'Drifting';
    case 'amendment_required':
      return 'Amendment required';
    case 'gap':
      return 'Verdict withheld';
    default:
      return status;
  }
}

function toneForStatus(status: DecisionConsoleDriftStatus): DriftTone {
  switch (status) {
    case 'on_track':
      return 'positive';
    case 'watch':
      return 'caution';
    case 'drifting':
    case 'amendment_required':
      return 'critical';
    case 'gap':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function sourceRefFromEvent(event: WatchEvent): string | null {
  if (event.series.kind === 'market_series') {
    return event.series.sourceRef ?? `${event.series.provider}:${event.series.sourceSeriesId}`;
  }
  return event.series.sourceRef ?? null;
}

function latestAsOf(bets: readonly DecisionConsoleBetV1[]): string | null {
  let latest: string | null = null;
  for (const bet of bets) {
    const asOf = bet.drift.asOf;
    if (asOf && (latest === null || asOf > latest)) latest = asOf;
  }
  return latest;
}

function finiteOrNull(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}

function formatUsd(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${trimZeros((value / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000) return `$${trimZeros((value / 1_000).toFixed(1))}K`;
  return `$${Math.round(value)}`;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}

function trimZeros(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}
